import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

const LOG_PREFIX = "[CreatorProfile lookup]";

/** Normalize a route handle segment for Firestore lookup. */
export function normalizeHandle(handle = "") {
  return handle.trim().replace(/^@+/, "").toLowerCase();
}

function toUser(docSnap) {
  return { uid: docSnap.id, ...docSnap.data() };
}

async function queryUsersByField(field, value, step) {
  console.log(`${LOG_PREFIX} Step ${step}: querying users where ${field} ==`, value);
  try {
    const snap = await getDocs(
      query(collection(db, "users"), where(field, "==", value))
    );
    console.log(`${LOG_PREFIX} Step ${step}: ${field} query returned ${snap.size} doc(s)`);
    if (snap.empty) return null;
    const user = toUser(snap.docs[0]);
    console.log(`${LOG_PREFIX} Step ${step}: matched uid ${user.uid}`);
    return user;
  } catch (err) {
    console.error(`${LOG_PREFIX} Step ${step}: ${field} query failed`, err?.code || err?.message || err);
    // Permission denied often looks like "not found" — bubble a clearer signal.
    if (err?.code === "permission-denied") {
      const e = new Error("Sign in to view creator profiles.");
      e.code = "permission-denied";
      throw e;
    }
    return null;
  }
}

/**
 * Resolve handle -> uid via the usernames registry (publicly readable),
 * then load the user document.
 */
export async function fetchUserByHandleViaRegistry(handle) {
  const normalized = normalizeHandle(handle);
  if (!normalized) return null;

  try {
    const registrySnap = await getDoc(doc(db, "usernames", normalized));
    if (!registrySnap.exists()) return null;

    const reg = registrySnap.data() || {};
    const now = new Date();
    const redirectUntil = reg.redirectUntil?.toDate
      ? reg.redirectUntil.toDate()
      : reg.redirectUntil
        ? new Date(reg.redirectUntil)
        : null;

    // 30-day redirect: old handle → current handle's user
    if (reg.redirectTo && redirectUntil && redirectUntil > now) {
      return fetchUserByHandleViaRegistry(reg.redirectTo);
    }

    // After redirect window, held handles do not resolve to a public profile
    const holdUntil = reg.handleHoldUntil?.toDate
      ? reg.handleHoldUntil.toDate()
      : reg.handleHoldUntil
        ? new Date(reg.handleHoldUntil)
        : null;
    if (reg.redirectTo && holdUntil && holdUntil > now) {
      return null;
    }

    const uid = reg.uid;
    if (!uid) return null;

    const userSnap = await getDoc(doc(db, "users", uid));
    if (!userSnap.exists()) return null;

    const user = toUser(userSnap);
    if (
      user.accountStatus === "deactivated"
      || user.accountStatus === "deleted"
      || user.profileHidden
    ) {
      return null;
    }

    return user;
  } catch (err) {
    console.error(`${LOG_PREFIX} Registry lookup failed`, err?.code || err?.message || err);
    if (err?.code === "permission-denied") {
      const e = new Error("Sign in to view creator profiles.");
      e.code = "permission-denied";
      throw e;
    }
    return null;
  }
}

/**
 * Resolve a /creator/:handle route param to a user document.
 * Order: usernames registry → handle → username → uid field → document id.
 */
export async function fetchUserByHandle(routeParam) {
  const clean = (routeParam || "").trim().replace(/^@+/, "");
  const normalized = clean.toLowerCase();

  if (!clean) {
    console.log(`${LOG_PREFIX} Empty route param — aborting`);
    return null;
  }

  console.log(`${LOG_PREFIX} Looking up creator for param "${clean}" (normalized: "${normalized}")`);

  // Step 0: public usernames registry (works even when handle field is missing on user docs)
  try {
    const fromRegistry = await fetchUserByHandleViaRegistry(normalized);
    if (fromRegistry) {
      console.log(`${LOG_PREFIX} Step 0: registry matched uid ${fromRegistry.uid}`);
      return fromRegistry;
    }
  } catch (err) {
    if (err?.code === "permission-denied") throw err;
  }

  // Step 1: handle field
  let user = await queryUsersByField("handle", normalized, 1);
  if (user) return user;
  if (clean !== normalized) {
    user = await queryUsersByField("handle", clean, "1b");
    if (user) return user;
  }

  // Step 2: username field
  user = await queryUsersByField("username", normalized, 2);
  if (user) return user;
  if (clean !== normalized) {
    user = await queryUsersByField("username", clean, "2b");
    if (user) return user;
  }

  // Step 3: uid field on the document
  user = await queryUsersByField("uid", clean, 3);
  if (user) return user;

  // Step 3 fallback: route param may be the Firestore document id
  console.log(`${LOG_PREFIX} Step 3 fallback: getDoc users/${clean}`);
  try {
    const snap = await getDoc(doc(db, "users", clean));
    console.log(`${LOG_PREFIX} Step 3 fallback: document exists =`, snap.exists());
    if (snap.exists()) {
      user = toUser(snap);
      console.log(`${LOG_PREFIX} Step 3 fallback: matched document id ${user.uid}`);
      return user;
    }
  } catch (err) {
    console.error(`${LOG_PREFIX} Step 3 fallback: getDoc failed`, err?.code || err?.message || err);
    if (err?.code === "permission-denied") {
      const e = new Error("Sign in to view creator profiles.");
      e.code = "permission-denied";
      throw e;
    }
  }

  console.log(`${LOG_PREFIX} All lookups failed for param "${clean}"`);
  return null;
}

/** @deprecated Use fetchUserByHandle */
export async function fetchCreatorByHandle(handle) {
  return fetchUserByHandle(handle);
}
